// Purchasing: ask suppliers for quotes, compare make / brand / price, pick one, raise the LPO (approved by amount),
// receive the goods, then match the supplier's invoice to what was ordered and received.
import { useState } from "react";
import { Page, Card, Btn, Tabs, Table, Tr, Td, Field, TextIn, Loading, Pill, money } from "@/components/kit";
import api from "@/lib/api";
import { act, useLoad, useCan, Select, DateIn, Num, n, Attach, opts } from "@/components/erp";
import { fmtDate, today } from "@/lib/money";

const TONE = { approved: "green", paid: "green", received: "green", sent: "blue", part_received: "amber", pending: "amber", open: "amber", quoting: "amber",
  held: "red", rejected: "red", cancelled: "grey", closed: "grey", converted: "grey", awarded: "grey" };
const CATS = [["spares", "Spares"], ["tyres", "Tyres"], ["batteries", "Batteries"], ["brake_linings", "Brake linings"], ["repairs", "Repairs & outside labour"], ["general", "General expenses"]]
  .map(([value, label]) => ({ value, label }));
const St = ({ s }) => <Pill kind={TONE[s] || "grey"}>{(s || "").replace("_", " ")}</Pill>;

export default function Purchasing() {
  const can = useCan();
  const [tab, setTab] = useState("lpos");
  const tabs = [
    { value: "lpos", label: "LPOs" },
    { value: "requests", label: "Requests" },
    { value: "quotes", label: "Quotes" },
    { value: "invoices", label: "Supplier invoices" },
    { value: "suppliers", label: "Suppliers" },
  ];
  return (
    <Page title="Purchasing">
      <Tabs value={tab} onChange={setTab} tabs={tabs} />
      {tab === "lpos" && <Lpos can={can} />}
      {tab === "requests" && <Requests can={can} goQuotes={() => setTab("quotes")} />}
      {tab === "quotes" && <Quotes can={can} goLpos={() => setTab("lpos")} />}
      {tab === "invoices" && <Invoices can={can} />}
      {tab === "suppliers" && <Suppliers can={can} />}
    </Page>
  );
}

// ---------------------------------------------------------------- suppliers
function Suppliers({ can }) {
  const { data, reload } = useLoad("/purchasing/suppliers");
  const [f, setF] = useState(null);
  if (!data) return <Loading />;
  const save = async () => {
    if (await act(() => api.post("/purchasing/suppliers", { ...f, terms_days: n(f.terms_days) || 0 }), "Added - Finance approves it before it can be used")) { setF(null); reload(); }
  };
  return (
    <>
      {can("suppliers.manage") && !f && <div className="mt-3"><Btn kind="primary" onClick={() => setF({ pay_to: "bank", terms: "credit", terms_days: 30 })}>+ New supplier</Btn></div>}
      {f && (
        <Card title="New supplier" className="mt-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Name"><TextIn value={f.name} onChange={(v) => setF({ ...f, name: v })} /></Field>
            <Field label="Phone"><TextIn value={f.phone} onChange={(v) => setF({ ...f, phone: v })} /></Field>
            <Field label="Email"><TextIn value={f.email} onChange={(v) => setF({ ...f, email: v })} /></Field>
            <Field label="KRA PIN"><TextIn value={f.kra_pin} onChange={(v) => setF({ ...f, kra_pin: v })} /></Field>
            <Field label="Cash or credit?"><Select empty={null} value={f.terms} onChange={(v) => setF({ ...f, terms: v, terms_days: v === "cash" ? 0 : 30 })}
              options={[{ value: "credit", label: "Credit - paid later" }, { value: "cash", label: "Cash - paid on delivery" }]} /></Field>
            {f.terms === "credit" && <Field label="Days to pay"><Num value={f.terms_days} onChange={(v) => setF({ ...f, terms_days: v })} /></Field>}
            <Field label="Pays to"><Select empty={null} value={f.pay_to} onChange={(v) => setF({ ...f, pay_to: v })}
              options={[["bank", "Bank account"], ["cheque", "Cheque"], ["mpesa", "M-Pesa number"], ["paybill", "Paybill"], ["till", "Till"], ["cash", "Cash"]].map(([value, label]) => ({ value, label }))} /></Field>
            <Field label="Account / number"><TextIn value={f.pay_details} onChange={(v) => setF({ ...f, pay_details: v })} /></Field>
          </div>
          <div className="mt-3 flex gap-2"><Btn kind="primary" onClick={save}>Save</Btn><Btn onClick={() => setF(null)}>Cancel</Btn></div>
        </Card>)}
      <Table minWidth={760} cols={[{ label: "Supplier" }, { label: "Contact" }, { label: "KRA PIN" }, { label: "Pays to" }, { label: "Terms" }, { label: "Status" }]}>
        {data.map((s) => (
          <Tr key={s.id}><Td className="font-medium">{s.name}</Td><Td>{[s.phone, s.email].filter(Boolean).join(" · ")}</Td><Td>{s.kra_pin}</Td>
            <Td>{s.pay_to} {s.pay_details}</Td><Td><Pill kind={s.terms === "cash" ? "amber" : "blue"}>{s.terms === "cash" ? "Cash" : `Credit ${s.terms_days} days`}</Pill></Td><Td><St s={s.status} /></Td></Tr>))}
      </Table>
      {!data.length && <p className="mt-4 text-sm text-slate-500">No suppliers yet.</p>}
    </>
  );
}

// ---------------------------------------------------------------- requests
function Requests({ can, goQuotes }) {
  const { data, reload } = useLoad("/purchasing/requisitions");
  const sup = useLoad(can("lpo.create") ? "/purchasing/suppliers" : null);
  const stock = useLoad("/stores/items");
  const [f, setF] = useState(null);
  const [ask, setAsk] = useState(null);
  if (!data) return <Loading />;
  const save = async () => {
    const lines = f.lines.filter((l) => l.description).map((l) => ({ description: l.description, qty: n(l.qty) || 0, unit: l.unit || "pcs", category: l.category || "general", item_id: l.item_id || null }));
    if (await act(() => api.post("/purchasing/requisitions", { lines, reason: f.reason || "", needed_by: f.needed_by || null }), "Request sent to procurement")) { setF(null); reload(); }
  };
  const ready = (sup.data || []).filter((s) => s.status === "approved" && s.active !== false);
  return (
    <>
      {can("lpo.request", "lpo.create") && !f && <div className="mt-3"><Btn kind="primary" onClick={() => setF({ lines: [{}], needed_by: "" })}>+ New purchase request</Btn></div>}
      {f && (
        <Card title="What is needed" className="mt-3">
          {f.lines.map((l, i) => (
            <div key={i} className="mb-2 grid gap-2 sm:grid-cols-[1fr_100px_100px_190px]">
              {(stock.data || []).length > 0 && (
                <div className="sm:col-span-4"><Select empty="Not a stores item - or pick one to stock it when received" label="From the stores" value={l.item_id || ""} options={opts(stock.data, (x) => `${x.code} · ${x.name} (${x.on_hand} in stock)`)}
                  onChange={(v) => { const it = (stock.data || []).find((x) => x.id === v); setF({ ...f, lines: f.lines.map((x, j) => (j === i ? { ...x, item_id: v || null, ...(it ? { description: it.name, unit: it.unit, category: it.category } : {}) } : x)) }); }} /></div>)}
              <TextIn placeholder="Item (e.g. Oil filter, FH12)" value={l.description} onChange={(v) => setF({ ...f, lines: f.lines.map((x, j) => (j === i ? { ...x, description: v } : x)) })} />
              <Num placeholder="Qty" value={l.qty} onChange={(v) => setF({ ...f, lines: f.lines.map((x, j) => (j === i ? { ...x, qty: v } : x)) })} />
              <TextIn placeholder="Unit" value={l.unit} onChange={(v) => setF({ ...f, lines: f.lines.map((x, j) => (j === i ? { ...x, unit: v } : x)) })} />
              <Select empty={null} label="What is it" value={l.category || "general"} options={CATS} onChange={(v) => setF({ ...f, lines: f.lines.map((x, j) => (j === i ? { ...x, category: v } : x)) })} />
            </div>))}
          <Btn onClick={() => setF({ ...f, lines: [...f.lines, {}] })}>+ Another item</Btn>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <Field label="Why / for which truck"><TextIn value={f.reason} onChange={(v) => setF({ ...f, reason: v })} /></Field>
            <Field label="Needed by"><DateIn value={f.needed_by} onChange={(v) => setF({ ...f, needed_by: v })} /></Field>
          </div>
          <div className="mt-3 flex gap-2"><Btn kind="primary" onClick={save}>Send</Btn><Btn onClick={() => setF(null)}>Cancel</Btn></div>
        </Card>)}
      {ask && (
        <Card title={`Ask for quotes - ${ask.req.no}`} className="mt-3">
          <p className="text-sm text-slate-600">Tick the suppliers to ask:</p>
          <div className="mt-2 flex flex-wrap gap-3">{ready.map((s) => (
            <label key={s.id} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={ask.ids.includes(s.id)}
              onChange={(e) => setAsk({ ...ask, ids: e.target.checked ? [...ask.ids, s.id] : ask.ids.filter((x) => x !== s.id) })} />{s.name}</label>))}
            {!ready.length && <span className="text-sm text-slate-500">No approved suppliers yet.</span>}</div>
          <div className="mt-3 flex gap-2">
            <Btn kind="primary" disabled={!ask.ids.length} onClick={async () => { if (await act(() => api.post("/purchasing/rfqs", { requisition_id: ask.req.id, supplier_ids: ask.ids }), "Quote request opened")) { setAsk(null); reload(); goQuotes(); } }}>Ask these suppliers</Btn>
            <Btn onClick={() => setAsk(null)}>Cancel</Btn></div>
        </Card>)}
      <Table minWidth={720} cols={[{ label: "No." }, { label: "What" }, { label: "Why" }, { label: "By" }, { label: "Status" }, { label: "" }]}>
        {data.map((r) => (
          <Tr key={r.id}><Td className="font-medium">{r.no}</Td><Td>{r.lines.map((l) => `${l.qty} ${l.unit} ${l.description}`).join("; ")}</Td><Td>{r.reason}</Td>
            <Td>{r.requested_name}<div className="text-xs text-slate-500">{fmtDate((r.created_at || "").slice(0, 10))}</div></Td><Td><St s={r.status} /></Td>
            <Td>{r.status === "open" && can("lpo.create") && <Btn onClick={() => setAsk({ req: r, ids: [] })}>Ask for quotes</Btn>}
              {r.status === "open" && <Btn onClick={async () => { if (await act(() => api.post(`/purchasing/requisitions/${r.id}/cancel`), "Cancelled")) reload(); }}>Cancel</Btn>}</Td></Tr>))}
      </Table>
      {!data.length && <p className="mt-4 text-sm text-slate-500">No requests yet.</p>}
    </>
  );
}

// ---------------------------------------------------------------- quotes
function Quotes({ can, goLpos }) {
  const { data, reload } = useLoad("/purchasing/rfqs");
  const [open, setOpen] = useState(null);
  if (!data) return <Loading />;
  if (open) return <Compare id={open} can={can} back={() => { setOpen(null); reload(); }} goLpos={goLpos} />;
  return (
    <>
      <Table minWidth={720} cols={[{ label: "No." }, { label: "What" }, { label: "Asked" }, { label: "Quotes in" }, { label: "Status" }, { label: "" }]}>
        {data.map((r) => (
          <Tr key={r.id}><Td className="font-medium">{r.no}</Td><Td>{r.lines.map((l) => `${l.qty} ${l.unit} ${l.description}`).join("; ")}</Td>
            <Td>{r.invited.map((i) => i.name).join(", ")}</Td><Td>{r.quotes.length} of {r.invited.length}</Td><Td><St s={r.status} /></Td>
            <Td><Btn onClick={() => setOpen(r.id)}>Open</Btn></Td></Tr>))}
      </Table>
      {!data.length && <p className="mt-4 text-sm text-slate-500">No quote requests yet. Open a purchase request and press "Ask for quotes".</p>}
    </>
  );
}

function Compare({ id, can, back, goLpos }) {
  const { data: r, reload } = useLoad(`/purchasing/rfqs/${id}`);
  const [q, setQ] = useState(null);
  if (!r) return <Loading />;
  const startQuote = (sid) => setQ({ supplier_id: sid, lines: r.lines.map(() => ({})), files: [] });
  const saveQuote = async () => {
    const lines = q.lines.map((l, i) => ({ line: i, brand: l.brand || "", unit_price: n(l.price) || 0, lead_days: n(l.lead) || 0 })).filter((l) => l.unit_price > 0);
    if (await act(() => api.post(`/purchasing/rfqs/${id}/quotes`, { supplier_id: q.supplier_id, lines, notes: q.notes || "", attachments: q.files }), "Quote recorded")) { setQ(null); reload(); }
  };
  const award = async (quote) => {
    let reason = "";
    if (r.quotes.length < 2 || !quote.cheapest) {
      reason = window.prompt(r.quotes.length < 2 ? "Only one quote - why buy now?" : "This isn't the cheapest quote - why is it chosen?") || "";
      if (!reason.trim()) return;
    }
    const lpo = await act(() => api.post(`/purchasing/rfqs/${id}/award`, { quote_id: quote.id, date: today(), reason }), (d) => `${d.no} raised - waiting for approval`);
    if (lpo) goLpos();
  };
  return (
    <>
      <div className="mt-3"><Btn onClick={back}>← All quote requests</Btn></div>
      <h2 className="mt-3 text-[15px] font-semibold">{r.no} <St s={r.status} /></h2>
      <Table minWidth={Math.max(640, 200 + r.quotes.length * 200)} cols={[{ label: "Item" }, { label: "Qty", align: "right" }, ...r.quotes.map((x) => ({ label: x.supplier }))]}
        foot={<Tr><Td colSpan={2}>Total</Td>{r.quotes.map((x) => (
          <Td key={x.id} className={x.cheapest ? "text-emerald-700" : ""}>{x.complete ? money(x.total) : "incomplete"}{x.cheapest && <div className="text-xs font-normal">cheapest</div>}</Td>))}</Tr>}>
        {r.lines.map((l, i) => (
          <Tr key={i}><Td>{l.description}</Td><Td right>{l.qty} {l.unit}</Td>
            {r.quotes.map((x) => { const p = x.lines.find((y) => y.line === i); return (
              <Td key={x.id}>{p ? <><b>{money(p.unit_price)}</b>{p.brand && <div className="text-xs text-slate-600">{p.brand}</div>}{p.lead_days ? <div className="text-xs text-slate-500">{p.lead_days} days</div> : null}</> : "—"}</Td>); })}
          </Tr>))}
        <Tr><Td colSpan={2} />{r.quotes.map((x) => (
          <Td key={x.id}>{r.status === "open" && can("lpo.create") && x.complete && <Btn kind="primary" onClick={() => award(x)}>Choose</Btn>}</Td>))}</Tr>
      </Table>
      {r.status === "open" && r.waiting.length > 0 && (
        <p className="mt-3 text-sm text-slate-600">Waiting for: {r.waiting.join(", ")}. {can("lpo.create") && r.invited.filter((i) => r.waiting.includes(i.name)).map((i) => (
          <Btn key={i.supplier_id} className="ml-2" onClick={() => startQuote(i.supplier_id)}>Enter {i.name}'s quote</Btn>))}</p>)}
      {r.award_reason && <p className="mt-3 text-sm text-slate-600">Why chosen: {r.award_reason}</p>}
      {q && (
        <Card title="Enter the quote as the supplier sent it" className="mt-3">
          {r.lines.map((l, i) => (
            <div key={i} className="mb-2 grid gap-2 sm:grid-cols-[1fr_160px_120px_90px]">
              <div className="self-center text-sm">{l.qty} {l.unit} {l.description}</div>
              <TextIn placeholder="Make / brand" value={q.lines[i].brand} onChange={(v) => setQ({ ...q, lines: q.lines.map((x, j) => (j === i ? { ...x, brand: v } : x)) })} />
              <Num placeholder="Unit price" value={q.lines[i].price} onChange={(v) => setQ({ ...q, lines: q.lines.map((x, j) => (j === i ? { ...x, price: v } : x)) })} />
              <Num placeholder="Days" value={q.lines[i].lead} onChange={(v) => setQ({ ...q, lines: q.lines.map((x, j) => (j === i ? { ...x, lead: v } : x)) })} />
            </div>))}
          <Attach value={q.files} onChange={(v) => setQ({ ...q, files: v })} label="The supplier's quotation (photo or PDF)" />
          <div className="mt-3 flex gap-2"><Btn kind="primary" onClick={saveQuote}>Save quote</Btn><Btn onClick={() => setQ(null)}>Cancel</Btn></div>
        </Card>)}
    </>
  );
}

// ---------------------------------------------------------------- LPOs
function Lpos({ can }) {
  const { data, reload } = useLoad("/purchasing/lpos");
  const [open, setOpen] = useState(null);
  if (!data) return <Loading />;
  if (open) return <LpoDetail id={open} can={can} back={() => { setOpen(null); reload(); }} />;
  return (
    <>
      <Table minWidth={760} cols={[{ label: "LPO" }, { label: "Supplier" }, { label: "Date" }, { label: "Total", align: "right" }, { label: "Goods in", align: "right" }, { label: "Status" }, { label: "" }]}>
        {data.map((d) => (
          <Tr key={d.id}><Td className="font-medium">{d.no}</Td><Td>{d.supplier}</Td><Td>{fmtDate(d.date)}</Td><Td right>{money(d.total)}</Td>
            <Td right>{money(d.received_value)}</Td><Td><St s={d.status} /></Td><Td><Btn onClick={() => setOpen(d.id)}>Open</Btn></Td></Tr>))}
      </Table>
      {!data.length && <p className="mt-4 text-sm text-slate-500">No LPOs yet. They are raised by choosing a quote.</p>}
    </>
  );
}

function LpoDetail({ id, can, back }) {
  const { data: d, reload } = useLoad(`/purchasing/lpos/${id}`);
  const [rec, setRec] = useState(null);
  const [inv, setInv] = useState(null);
  if (!d) return <Loading />;
  const run = async (path, ok) => { if (await act(() => api.post(`/purchasing/lpos/${id}/${path}`), ok)) reload(); };
  const receive = async () => {
    const lines = d.lines.map((l, i) => ({ line: i, qty: n(rec.q[i]) || 0 })).filter((l) => l.qty > 0);
    if (await act(() => api.post(`/purchasing/lpos/${id}/receive`, { date: rec.date, delivery_note: rec.dn, lines, attachments: rec.files }), "Goods received")) { setRec(null); reload(); }
  };
  const capture = async () => {
    if (await act(() => api.post("/purchasing/invoices", { lpo_id: id, invoice_no: inv.no, date: inv.date, amount: n(inv.amount), vat: n(inv.vat) || 0, attachments: inv.files }), (x) => (x.status === "held" ? "Captured but HELD - it doesn't match" : "Captured - sent for payment approval"))) { setInv(null); reload(); }
  };
  return (
    <>
      <div className="mt-3"><Btn onClick={back}>← All LPOs</Btn></div>
      <h2 className="mt-3 text-[15px] font-semibold">{d.no} · {d.supplier} <St s={d.status} /></h2>
      <p className="text-sm text-slate-600">Raised by {d.created_name} on {fmtDate(d.date)} · total {money(d.total)} · received {money(d.received_value)} · invoiced {money(d.invoiced)}</p>
      {d.award_reason && <p className="text-sm text-slate-600">Why this quote: {d.award_reason}</p>}
      <Table minWidth={640} cols={[{ label: "Item" }, { label: "Brand" }, { label: "Ordered", align: "right" }, { label: "Received", align: "right" }, { label: "Price", align: "right" }, { label: "Value", align: "right" }]}>
        {d.lines.map((l, i) => <Tr key={i}><Td>{l.description}</Td><Td>{l.brand}</Td><Td right>{l.qty} {l.unit}</Td><Td right>{l.received}</Td><Td right>{money(l.unit_price)}</Td><Td right>{money(l.qty * l.unit_price)}</Td></Tr>)}
      </Table>
      <div className="mt-3 flex flex-wrap gap-2">
        {d.status === "approved" && can("lpo.send") && <Btn kind="primary" onClick={() => run("send", "Sent to the supplier")}>Mark as sent to supplier</Btn>}
        {["sent", "part_received"].includes(d.status) && can("stores.receive") && <Btn kind="primary" onClick={() => setRec({ date: today(), dn: "", q: {}, files: [] })}>Receive goods</Btn>}
        {["sent", "part_received", "received"].includes(d.status) && can("supplier_invoice.capture") && <Btn onClick={() => setInv({ date: today(), files: [] })}>Capture supplier invoice</Btn>}
        {["sent", "part_received", "received"].includes(d.status) && can("lpo.create", "approve.final") && <Btn onClick={() => run("close", "Closed")}>Close (no more coming)</Btn>}
        {["pending", "approved", "sent"].includes(d.status) && !d.lines.some((l) => l.received) && <Btn onClick={() => run("cancel", "Cancelled")}>Cancel LPO</Btn>}
      </div>
      {rec && (
        <Card title="Goods received" className="mt-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Date"><DateIn value={rec.date} onChange={(v) => setRec({ ...rec, date: v })} /></Field>
            <Field label="Supplier's delivery note no."><TextIn value={rec.dn} onChange={(v) => setRec({ ...rec, dn: v })} /></Field>
          </div>
          {d.lines.map((l, i) => (
            <div key={i} className="mt-2 grid items-center gap-2 sm:grid-cols-[1fr_140px]">
              <div className="text-sm">{l.description} <span className="text-slate-500">(ordered {l.qty}, in {l.received})</span></div>
              <Num placeholder="Qty now" value={rec.q[i]} onChange={(v) => setRec({ ...rec, q: { ...rec.q, [i]: v } })} />
            </div>))}
          <div className="mt-2"><Attach value={rec.files} onChange={(v) => setRec({ ...rec, files: v })} label="Photo of the delivery note / goods" /></div>
          <div className="mt-3 flex gap-2"><Btn kind="primary" onClick={receive}>Save</Btn><Btn onClick={() => setRec(null)}>Cancel</Btn></div>
        </Card>)}
      {inv && (
        <Card title="Supplier invoice" className="mt-3">
          <div className="grid gap-3 sm:grid-cols-4">
            <Field label="Invoice no."><TextIn value={inv.no} onChange={(v) => setInv({ ...inv, no: v })} /></Field>
            <Field label="Invoice date"><DateIn value={inv.date} onChange={(v) => setInv({ ...inv, date: v })} /></Field>
            <Field label="Total (VAT included)"><Num value={inv.amount} onChange={(v) => setInv({ ...inv, amount: v })} /></Field>
            <Field label="of which VAT"><Num value={inv.vat} onChange={(v) => setInv({ ...inv, vat: v })} /></Field>
          </div>
          <p className="mt-2 text-xs text-slate-500">The match is on the amount before VAT, against what was ordered and received.</p>
          <div className="mt-2"><Attach value={inv.files} onChange={(v) => setInv({ ...inv, files: v })} label="The invoice (photo or PDF)" /></div>
          <div className="mt-3 flex gap-2"><Btn kind="primary" onClick={capture}>Capture and match</Btn><Btn onClick={() => setInv(null)}>Cancel</Btn></div>
        </Card>)}
      {d.grns.length > 0 && <Card title="Goods received notes" className="mt-3">{d.grns.map((g) => (
        <div key={g.id} className="text-sm">{g.no} · {fmtDate(g.date)} · delivery note {g.delivery_note} · {g.lines.map((l) => `${l.qty} ${l.description}`).join(", ")} · by {g.received_name}</div>))}</Card>}
      {d.invoices.length > 0 && <Card title="Invoices" className="mt-3">{d.invoices.map((i) => (
        <div key={i.id} className="text-sm">{i.invoice_no} · {money(i.amount)} · <St s={i.status} />{i.issues?.length ? ` · ${i.issues.join("; ")}` : ""}</div>))}</Card>}
    </>
  );
}

// ---------------------------------------------------------------- supplier invoices
function Invoices({ can }) {
  const { data, reload } = useLoad("/purchasing/invoices");
  if (!data) return <Loading />;
  const call = async (i, path, body, ok) => { if (await act(() => api.post(`/purchasing/invoices/${i.id}/${path}`, body), ok)) reload(); };
  return (
    <>
      <Table minWidth={900} cols={[{ label: "Supplier" }, { label: "Invoice" }, { label: "LPO" }, { label: "Amount", align: "right" }, { label: "Due" }, { label: "Match" }, { label: "Status" }, { label: "" }]}>
        {data.map((i) => (
          <Tr key={i.id}><Td className="font-medium">{i.supplier}</Td><Td>{i.invoice_no}<div className="text-xs text-slate-500">{fmtDate(i.date)}</div></Td><Td>{i.lpo_no}</Td>
            <Td right>{money(i.amount)}<div className="text-xs text-slate-500">received {money(i.received_value)}</div></Td>
            <Td>{i.terms === "cash" ? <Pill kind="amber">cash</Pill> : <span className={i.status !== "paid" && i.due_date < today() ? "font-medium text-rose-700" : ""}>{fmtDate(i.due_date)}</span>}
              {i.books === "not posted" && <div className="text-xs text-amber-700">not in the books: {i.books_note}</div>}</Td>
            <Td>{i.match === "ok" ? <Pill kind="green">matches</Pill> : <div className="text-xs text-rose-700">{(i.issues || []).join("; ")}</div>}</Td><Td><St s={i.status} /></Td>
            <Td className="space-x-2 whitespace-nowrap">
              {i.status === "held" && can("approve.final") && <Btn onClick={() => { const reason = window.prompt("Why is it paid although it doesn't match?"); if (reason) call(i, "override", { reason }, "Released for payment approval"); }}>Release</Btn>}
              {i.status === "approved" && can("payment.initiate") && <Btn kind="primary" onClick={() => { const method = (window.prompt("Paid by: bank, cheque, cash or mpesa", "bank") || "").trim().toLowerCase(); if (!method) return; const reference = window.prompt(method === "cheque" ? "Cheque number" : "Payment reference (bank / M-Pesa code, or a cash voucher no.)"); if (reference) call(i, "paid", { reference, method }, "Recorded as paid"); }}>Mark paid</Btn>}
              {["held", "pending", "approved"].includes(i.status) && can("supplier_invoice.capture", "approve.final") && <Btn onClick={() => call(i, "cancel", {}, "Cancelled")}>Cancel</Btn>}
            </Td></Tr>))}
      </Table>
      {!data.length && <p className="mt-4 text-sm text-slate-500">No supplier invoices yet. Capture one from an LPO once goods have come in.</p>}
    </>
  );
}
